import React, { useEffect, useState } from 'react';
import { SharedTestResult, SpeedTestResult, LinkQuality } from '../types';
import { DownloadIcon, UploadIcon, PingIcon, JitterIcon, ShieldCheckIcon, ShieldExclamationIcon, InfoIcon } from 'shared-icons';
import { calculateLinkQuality, formatSpeed } from 'shared-grading';

// These components are duplicated from PromptInput for simplicity, as they are display-only here.
// In a larger app, they might be moved to a shared directory.

const useCountUp = (end: number, duration: number = 1000) => {
  const [count, setCount] = React.useState(0);
  const frameRef = React.useRef<number | null>(null);
  const startTimeRef = React.useRef<number | null>(null);
  const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

  React.useEffect(() => {
    startTimeRef.current = null;
    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      const currentVal = easeOutCubic(progress) * end;
      setCount(currentVal);
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if(frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [end, duration]);

  return count;
};

const AnimatedNumber: React.FC<{ value: number; duration?: number; decimals?: number }> = ({ value, duration = 1500, decimals = 2 }) => {
  const animatedValue = useCountUp(value, duration);
  return <>{animatedValue.toFixed(decimals)}</>;
};

const LinkQualityDisplay: React.FC<{ quality: LinkQuality }> = ({ quality }) => {
    const Icon = quality.grade === 'A' || quality.grade === 'B' ? ShieldCheckIcon : ShieldExclamationIcon;
    const [barWidth, setBarWidth] = useState(0);

    useEffect(() => {
        // Trigger the animation after the component mounts
        const timer = setTimeout(() => setBarWidth(quality.score), 100);
        return () => clearTimeout(timer);
    }, [quality.score]);

    if (quality.isError) {
        return (
             <div className="w-full text-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-center gap-3">
                    <InfoIcon width={28} height={28} className={quality.color} />
                    <div>
                        <h3 className={`text-xl font-bold ${quality.color}`}>{quality.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-slate-400">{quality.description}</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full text-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
            <div className="flex items-center justify-center gap-3">
                <Icon width={32} height={32} className={quality.color} />
                <div>
                    <h3 className={`text-xl font-bold ${quality.color}`}>{quality.title} (Grade {quality.grade})</h3>
                     <p className="text-sm text-gray-600 dark:text-slate-400">{quality.description}</p>
                </div>
            </div>
            <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                    <div
                        className={`h-2.5 rounded-full ${quality.color.replace('text-', 'bg-')}`}
                        style={{ width: `${barWidth}%`, transition: 'width 1.5s cubic-bezier(0.25, 1, 0.5, 1)' }}
                    ></div>
                </div>
            </div>
            {quality.historyContext && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-2 italic">{quality.historyContext}</p>
            )}
        </div>
    );
};


const ResultPage: React.FC<{ result: SharedTestResult }> = ({ result }) => {
  const quality = calculateLinkQuality(result.results); // No history available for shared results
  const formattedDownload = formatSpeed(result.results.download);
  const formattedUpload = formatSpeed(result.results.upload);
  
  const handleRunTest = () => {
    // Navigate to the base URL to start a new test
    window.location.href = window.location.origin + window.location.pathname;
  };

  return (
    <div className="w-full max-w-lg text-center flex flex-col items-center gap-6 p-4 animate-fade-in">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Speed Test Result</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Test completed on {new Date(result.date).toLocaleString()}
        </p>
      </div>

      <LinkQualityDisplay quality={quality} />

      <div className="w-full grid grid-cols-2 gap-4">
            <div className="flex flex-col items-center justify-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-cyan-500 dark:text-cyan-400">
                    <DownloadIcon />
                    <span>Download</span>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white">
                    <AnimatedNumber value={parseFloat(formattedDownload.value)} decimals={formattedDownload.value.includes('.') ? formattedDownload.value.split('.')[1].length : 0} />
                </div>
                <div className="text-gray-500 dark:text-slate-500">{formattedDownload.unit}</div>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-purple-500 dark:text-purple-400">
                    <UploadIcon />
                    <span>Upload</span>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white">
                    <AnimatedNumber value={parseFloat(formattedUpload.value)} decimals={formattedUpload.value.includes('.') ? formattedUpload.value.split('.')[1].length : 0} />
                </div>
                <div className="text-gray-500 dark:text-slate-500">{formattedUpload.unit}</div>
            </div>
             <div className="flex flex-col items-center justify-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-slate-400">
                    <PingIcon />
                    <span>Ping</span>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white"><AnimatedNumber value={result.results.ping} decimals={0} /></div>
                <div className="text-gray-500 dark:text-slate-500">ms</div>
            </div>
            <div className="flex flex-col items-center justify-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-slate-400">
                    <JitterIcon />
                    <span>Jitter</span>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-white"><AnimatedNumber value={result.results.jitter} decimals={0} /></div>
                <div className="text-gray-500 dark:text-slate-500">ms</div>
            </div>
        </div>
      
      <div className="w-full text-center p-4 bg-gray-100 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg space-y-2">
        <div>
          <span className="font-semibold text-slate-800 dark:text-slate-200">ISP:</span>
          <span className="text-gray-600 dark:text-slate-300 ml-2">{result.connectionInfo.isp}</span>
        </div>
        <div>
          <span className="font-semibold text-slate-800 dark:text-slate-200">Your Location:</span>
          <span className="text-gray-600 dark:text-slate-300 ml-2">{result.connectionInfo.serverLocation}</span>
        </div>
        <div>
          <span className="font-semibold text-slate-800 dark:text-slate-200">Test Server:</span>
          <span className="text-gray-600 dark:text-slate-300 ml-2">{result.server.name} ({result.server.location})</span>
        </div>
      </div>
      
      <button
        onClick={handleRunTest}
        className="mt-4 px-8 py-4 bg-gradient-to-br from-cyan-500 to-purple-600 text-white text-xl font-bold rounded-full shadow-lg transform transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500/50"
      >
        Run Your Own Speed Test
      </button>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default ResultPage;